/* eslint-disable camelcase */
import { requestJSON } from './request'

export interface Profile {
  username: string
  forum_user: {
    avatar_url: string
    biography: string
  }
}

export interface Name {
  first_name: string
  last_name: string
}

export interface Biography {
  biography: string
}

export interface Links {
  links_array: object[]
}

export interface ProfileLink {
  id: number
  forum_user: number
  url: string
  link_type: number
}

export interface ProfileAvatar {
  user: number
  avatar: string
  avatar_url: string
  biography: string
}

export interface IsPremium {
  is_premium: boolean
}

export interface DateTimeModified {
  date_modified: string
}

export async function getProfile (profileId: number): Promise<Profile> {
  const url = new URL(`/api/profile/${profileId}/`, window.location.href)
  return await requestJSON(url.toString())
}

export async function updateProfileName (profileId: number, body: Name): Promise<Name> {
  return await requestJSON(`/api/user/${profileId}/`, { method: 'PUT', body })
}

export async function updateProfileBiography (profileId: number, body: Biography): Promise<Biography> {
  return await requestJSON(`/api/profile/${profileId}/`, { method: 'PUT', body })
}

export async function getProfileLinks (): Promise<ProfileLink[]> {
  const url = new URL('/api/profile-links/', window.location.href)
  return await requestJSON(url.toString())
}

export async function getLinkById (linkId: number): Promise<ProfileLink> {
  return await requestJSON(`/api/project-links/${linkId}`)
}

export async function postProfileLink (body: object): Promise<object> {
  return await requestJSON('/api/profile-links/', { method: 'POST', body })
}

export async function updateProfileLink (linkId: number, body: object): Promise<object> {
  return await requestJSON(`/api/profile-links/${linkId}/`, { method: 'PUT', body })
}

export async function deleteProfileLink (linkId: number, body: object): Promise<object> {
  return await requestJSON(`/api/profile-links/${linkId}/`, { method: 'DELETE', body })
}

export async function getProfileAvatar (profileId: number): Promise<ProfileAvatar> {
  const url = new URL(`/api/profile/${profileId}/`, window.location.href)
  return await requestJSON(url.toString())
}

export async function updateDateModified (profileId: number, body: DateTimeModified): Promise<DateTimeModified> {
  return await requestJSON(`/api/profile/${profileId}/`, { method: 'PUT', body })
}

// Membership
export enum MembershipType {
  individual = 0,
  collective = 1
}

export interface ProfileMembership {
  id: number
  forum_user: number
  date_start: string
  date_end: string
  type: MembershipType
  keys: ProfilePrivateActivationKey[]
  is_valid: boolean
}

export interface ProfileActivationKey {
  id: number
  date_start: string
  date_end: string
  key: string
}

interface ProfilePrivateActivationKey {
  id: number
  membership: ProfileMembership
}

interface generateActivationKeyResponse {
  pk: number
}

interface hasActiveMembershipResponse {
  has_active_membership: boolean
  user: number
}

export async function getProfileMembership (membershipId: number): Promise<ProfileMembership> {
  const url = new URL(`/api/profile-memberships/${membershipId}/`, window.location.href)
  return await requestJSON(url.toString())
}

export async function generateActivationKey (membershipId: number): Promise<generateActivationKeyResponse> {
  const url = new URL(`/api/profile-memberships/${membershipId}/generate_key/`, window.location.href)
  return await requestJSON(url.toString())
}

export async function getActivationKey (activationKeyId: number): Promise<ProfileActivationKey> {
  const url = new URL(`/api/profile-activation-keys/${activationKeyId}/`, window.location.href)
  return await requestJSON(url.toString())
}

export async function sendActivationKeyMail (activationKeyId: number): Promise<ProfileActivationKey> {
  const url = new URL(`/api/profile-activation-keys/${activationKeyId}/send/`, window.location.href)
  return await requestJSON(url.toString())
}

export async function updateHasActiveMembership (profileId: number): Promise<hasActiveMembershipResponse> {
  const url = new URL(`/api/profile/${profileId}/has_active_membership/`, window.location.href)
  return await requestJSON(url.toString(), { method: 'GET' })
}

// Settings
export interface EditProfileSettingsReq {
  id: number
  user: number
  notify_updated_contents?: boolean
  notify_new_project_discussion_threads?: boolean
  has_newsletter_subscription?: boolean
}

export async function editProfileSettings (profileId: number, body: EditProfileSettingsReq): Promise<void> {
  return await requestJSON(`/api/profile/${profileId}/`, { method: 'PUT', body })
}
